import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createBlock as _createBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  id: "modal",
  class: "modal fade ncr2-modal",
  tabindex: "-1",
  role: "dialog",
  "aria-labelledby": "ncr2-modal-title",
  "aria-hidden": "true"
}
const _hoisted_2 = {
  class: "modal-dialog",
  role: "document"
}
const _hoisted_3 = { class: "modal-content" }
const _hoisted_4 = { class: "modal-header ncr2-modal-header" }
const _hoisted_5 = {
  id: "ncr2-modal-title",
  class: "modal-title"
}
const _hoisted_6 = { class: "modal-body" }
const _hoisted_7 = {
  key: 0,
  class: "ncr-pane-alert is-error"
}
const _hoisted_8 = { class: "ncr2-details-grid" }
const _hoisted_9 = { class: "ncr2-field ncr2-field--part" }
const _hoisted_10 = { class: "ncr2-check-field" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = { class: "ncr2-field ncr2-field--quantity" }
const _hoisted_13 = { class: "ncr2-field ncr2-field--wide" }
const _hoisted_14 = { class: "ncr2-field ncr2-field--severity" }
const _hoisted_15 = { class: "ncr2-field ncr2-field--full" }
const _hoisted_16 = { class: "ncr2-field ncr2-field--third" }
const _hoisted_17 = { class: "ncr2-field ncr2-field--third" }
const _hoisted_18 = { class: "ncr2-field ncr2-field--third" }
const _hoisted_19 = {
  key: 1,
  class: "ncr2-create-files"
}
const _hoisted_20 = ["disabled"]
const _hoisted_21 = {
  key: 0,
  class: "ncr2-staged-files"
}
const _hoisted_22 = {
  key: 2,
  class: "ncr2-tabs"
}
const _hoisted_23 = {
  class: "ncr2-tab-list",
  "aria-label": "NCR details"
}
const _hoisted_24 = ["onClick"]
const _hoisted_25 = {
  key: 0,
  class: "ncr2-tab-count"
}
const _hoisted_26 = { class: "ncr2-tab-panel" }
const _hoisted_27 = {
  key: 1,
  class: "ncr2-capa-grid"
}
const _hoisted_28 = { class: "ncr2-field ncr2-field--full" }
const _hoisted_29 = { class: "ncr2-field" }
const _hoisted_30 = { class: "ncr2-field" }
const _hoisted_31 = ["href"]
const _hoisted_32 = { class: "ncr2-field" }
const _hoisted_33 = { class: "ncr2-field" }
const _hoisted_34 = { class: "modal-footer ncr2-modal-footer" }
const _hoisted_35 = { class: "ncr2-shelf-toggle" }
const _hoisted_36 = ["disabled"]
const _hoisted_37 = { class: "ncr2-modal-actions" }
const _hoisted_38 = ["disabled"]
const _hoisted_39 = ["disabled"]

import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import type { Unsubscribe } from 'firebase/firestore';

import { Input, SearchDropdown, Select, TextArea } from '@/components/ui';
import type { SelectOption } from '@/components/ui/select/Select';
import { uploadRecordFiles, validateLocalRecordFile } from '@/modules/common/files/RecordFileWorkflow';
import { fileClient } from '@/modules/common/files/FileClient';
import {
  createEmptyNcrForm,
  formFromRecord,
  ncrClient,
  type LegacyNcrFile,
  type NcrFormState,
  type NcrIssue,
  type NcrPart,
  type NcrRecord
} from '@/modules/ncr/NcrClient';
import { onDocumentModalEvent } from '@/components/modal-events';

import NcrActivityPane from './NcrActivityPane.vue';
import NcrFilesPane from './NcrFilesPane.vue';
import NcrMessagesPane from './NcrMessagesPane.vue';
import { registerNcrModalController, type NcrModalController } from './ncr-modal-controller';

type ModalMode = 'create' | 'edit' | 'read-only';
type TabKey = 'messages' | 'capa' | 'files' | 'activity';


export default /*@__PURE__*/_defineComponent({
  __name: 'NcrModal',
  setup(__props) {

const mode = ref<ModalMode>('create');
const record = ref<NcrRecord | null>(null);
const recordId = ref('');
const form = ref<NcrFormState>(createEmptyNcrForm());
const parts = ref<NcrPart[]>([]);
const partsLoading = ref(true);
const issues = ref<NcrIssue[]>([]);
const stagedFiles = ref<File[]>([]);
const activeTab = ref<TabKey>('messages');
const isSaving = ref(false);
const isPrinting = ref(false);
const errorMessage = ref('');
let unsubscribeRecord: Unsubscribe | null = null;
let disposeHiddenListener: (() => void) | null = null;
let openGeneration = 0;

const tabs: Array<{ key: TabKey; label: string }> = [
  { key: 'messages', label: 'Messages' },
  { key: 'capa', label: 'CAPA' },
  { key: 'files', label: 'Files' },
  { key: 'activity', label: 'Activity' }
];

const severityOptions: SelectOption[] = [
  { value: 'S1', label: 'S1 - Critical' },
  { value: 'S2', label: 'S2 - Major' },
  { value: 'S3', label: 'S3 - Minor' },
  { value: 'S4', label: 'S4 - Low' }
];
const stageOptions: SelectOption[] = [
  { value: '1', label: 'Incoming QC' },
  { value: '2', label: 'In Process' },
  { value: '3', label: 'Post Build' }
];
const statusOptions: SelectOption[] = [
  { value: 'Pending Review', label: 'Pending Review' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Escalated', label: 'Escalated' },
  { value: 'Vendor Return', label: 'Returned to Vendor' },
  { value: 'Monitoring', label: 'Closed - Monitoring' },
  { value: 'Closed', label: 'Closed' }
];
const dispositionOptions: SelectOption[] = [
  { value: '4', label: 'Use-as-is' },
  { value: '3', label: 'Rework' },
  { value: '5', label: 'Vendor Return' },
  { value: '2', label: 'Scrap' }
];

function partSearchHaystacks(part: NcrPart): string[] {
  return [
    part.part,
    part.description,
    part.product,
    part.type,
    part.class,
    ...(part.vendors || []).map(vendor => vendor.vendorcode || '')
  ].filter(Boolean);
}

const issueOptions = computed<SelectOption[]>(() => (
  issues.value.map(issue => ({ value: issue.id, label: `${issue.id}: ${issue.title}` }))
));
const modalTitle = computed(() => mode.value === 'create' ? 'New NCR' : recordId.value);
const isReadOnly = computed(() => mode.value === 'read-only');
const fileCount = computed(() => (
  Number(record.value?.fileSummary?.readyCount || 0)
  + (record.value?.files?.filter(file => !file.sourceFileId).length || 0)
));
const asanaUrl = computed(() => `https://app.asana.com/0/1202959342405780/${form.value.asana}/f`);

function modalService() {
  return (window as Window & {
    JidokaModal?: {
      show: (target: string, options?: { backdrop?: boolean | 'static'; keyboard?: boolean }) => void;
      hide: (target: string) => void;
    }
  }).JidokaModal;
}

function stopRecordSubscription(): void {
  unsubscribeRecord?.();
  unsubscribeRecord = null;
}

function subscribeRecord(id: string): void {
  stopRecordSubscription();
  unsubscribeRecord = ncrClient.subscribeToRecord(id, nextRecord => {
    record.value = nextRecord;
  });
}

async function show(): Promise<void> {
  await nextTick();
  modalService()?.show('#modal', { backdrop: 'static', keyboard: !isReadOnly.value });
}

function openCreate(): void {
  openGeneration += 1;
  stopRecordSubscription();
  mode.value = 'create';
  record.value = null;
  recordId.value = '';
  form.value = createEmptyNcrForm();
  stagedFiles.value = [];
  activeTab.value = 'messages';
  errorMessage.value = '';
  void show();
}

function renderEdit(nextRecord: NcrRecord): void {
  mode.value = 'edit';
  record.value = nextRecord;
  recordId.value = nextRecord.id;
  form.value = formFromRecord(nextRecord);
  stagedFiles.value = [];
  activeTab.value = 'messages';
  errorMessage.value = '';
  subscribeRecord(nextRecord.id);
  void show();
}

async function openEdit(nextRecord: NcrRecord | string): Promise<void> {
  const generation = ++openGeneration;
  if (typeof nextRecord === 'string') {
    const loadedRecord = await ncrClient.loadRecord(nextRecord);
    if (generation !== openGeneration) return;
    renderEdit(loadedRecord);
    return;
  }
  if (generation !== openGeneration) return;
  renderEdit(nextRecord);
}

async function openDeepLink(id: string): Promise<void> {
  const generation = ++openGeneration;
  errorMessage.value = '';
  try {
    const nextRecord = await ncrClient.loadRecord(id);
    if (generation !== openGeneration) return;
    renderEdit(nextRecord);
    mode.value = 'read-only';
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Unable to load NCR';
    throw error;
  }
}

function updateRecord(nextRecord: NcrRecord): void {
  if (nextRecord.id === recordId.value) {
    record.value = nextRecord;
  }
}

function validate(): string | null {
  if (!form.value.newpart) return 'Part number is required.';
  const quantity = Number.parseInt(form.value.quantity, 10);
  if (!Number.isInteger(quantity) || quantity < 1 || quantity > 9999) {
    return 'Quantity must be a whole number from 1 to 9999.';
  }
  if (!form.value.severity) return 'Severity is required.';
  if (!form.value.description.trim()) return 'Description is required.';
  if (!form.value.stage) return 'Stage is required.';
  if (!form.value.timelost.trim()) return 'Time lost is required.';
  return null;
}

async function save(): Promise<void> {
  const validationError = validate();
  if (validationError) {
    errorMessage.value = validationError;
    return;
  }

  isSaving.value = true;
  errorMessage.value = '';
  try {
    let saved: NcrRecord;
    if (mode.value === 'create') {
      saved = await ncrClient.create(form.value, parts.value);
      mode.value = 'edit';
      record.value = saved;
      recordId.value = saved.id;
      subscribeRecord(saved.id);
    } else {
      saved = await ncrClient.update(recordId.value, form.value, parts.value);
      record.value = saved;
    }

    for (const stagedFile of [...stagedFiles.value]) {
      const [uploadedFile] = await uploadRecordFiles({
        recordType: 'ncr',
        recordId: saved.id,
        files: [stagedFile]
      });
      const downloadUrl = await fileClient.getDownloadUrl(uploadedFile.storagePath);
      await ncrClient.addModernFileMirror(saved.id, uploadedFile, downloadUrl);
      stagedFiles.value = stagedFiles.value.filter(file => file !== stagedFile);
    }

    recordId.value = saved.id;
    close();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Unable to save NCR';
  } finally {
    isSaving.value = false;
  }
}

function stageFiles(event: Event): void {
  const selectedFiles = Array.from((event.target as HTMLInputElement).files || []);
  const validationError = selectedFiles.map(validateLocalRecordFile).find(Boolean);
  if (validationError) {
    errorMessage.value = validationError;
    (event.target as HTMLInputElement).value = '';
    return;
  }
  stagedFiles.value = selectedFiles;
}

async function removeLegacyFile(file: LegacyNcrFile): Promise<void> {
  if (!record.value || !window.confirm(`Delete ${file.name}?`)) return;
  try {
    const nextFiles = await ncrClient.removeLegacyFile(record.value.id, file, record.value.files || []);
    record.value = { ...record.value, files: nextFiles };
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Unable to delete file';
  }
}

async function printLabel(): Promise<void> {
  if (!record.value || isPrinting.value) return;
  isPrinting.value = true;
  errorMessage.value = '';
  try {
    const jobId = await ncrClient.print(record.value, parts.value);
    window.alert(jobId ? `NCR label queued. Job ID: ${jobId}` : 'NCR label queued.');
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Unable to print NCR label';
  } finally {
    isPrinting.value = false;
  }
}

function close(): void {
  modalService()?.hide('#modal');
}

function reset(): void {
  openGeneration += 1;
  stopRecordSubscription();
  record.value = null;
  recordId.value = '';
  form.value = createEmptyNcrForm();
  stagedFiles.value = [];
  activeTab.value = 'messages';
  errorMessage.value = '';
  isSaving.value = false;
  isPrinting.value = false;
}

const controller: NcrModalController = {
  create: openCreate,
  edit: openEdit,
  openDeepLink,
  updateRecord,
  reset
};

onMounted(() => {
  registerNcrModalController(controller);
  void ncrClient.loadParts()
    .then(nextParts => {
      parts.value = nextParts;
    })
    .catch(error => {
      console.warn('[NcrModal] Parts catalog failed to load:', error);
    })
    .finally(() => {
      partsLoading.value = false;
    });
  void ncrClient.loadIssues()
    .then(nextIssues => {
      issues.value = nextIssues;
    })
    .catch(error => {
      console.warn('[NcrModal] Issues failed to load:', error);
    });

  disposeHiddenListener = onDocumentModalEvent('hidden.bs.modal', (event: any) => {
    if (event?.target?.id === 'modal') reset();
  });
});

onBeforeUnmount(() => {
  stopRecordSubscription();
  disposeHiddenListener?.();
  if (window.NcrModalController === controller) delete window.NcrModalController;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("header", _hoisted_4, [
          _createElementVNode("h5", _hoisted_5, _toDisplayString(modalTitle.value), 1 /* TEXT */),
          _createElementVNode("button", {
            type: "button",
            class: "close ncr2-modal-close",
            "aria-label": "Close",
            onClick: close
          }, [...(_cache[15] || (_cache[15] = [
            _createElementVNode("span", { "aria-hidden": "true" }, "×", -1 /* CACHED */)
          ]))])
        ]),
        _createElementVNode("form", {
          class: "ncr2-modal-form",
          onSubmit: _withModifiers(save, ["prevent"])
        }, [
          _createElementVNode("div", _hoisted_6, [
            (errorMessage.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(errorMessage.value), 1 /* TEXT */))
              : _createCommentVNode("v-if", true),
            _createElementVNode("section", _hoisted_8, [
              _createElementVNode("div", _hoisted_9, [
                _createVNode(_unref(SearchDropdown), {
                  modelValue: form.value.newpart,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.value.newpart) = $event)),
                  items: parts.value,
                  "value-path": "part",
                  "subtitle-path": "description",
                  "get-search-texts": partSearchHaystacks,
                  label: "Part Number",
                  required: "",
                  "output-mode": "valueWithSubtitle",
                  variant: "form",
                  "min-chars": 2,
                  loading: partsLoading.value,
                  disabled: isReadOnly.value || isSaving.value,
                  "aria-label": "Part number"
                }, null, 8 /* PROPS */, ["modelValue", "items", "loading", "disabled"])
              ]),
              _createElementVNode("label", _hoisted_10, [
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.process) = $event)),
                  type: "checkbox",
                  disabled: isReadOnly.value || isSaving.value
                }, null, 8 /* PROPS */, _hoisted_11), [
                  [_vModelCheckbox, form.value.process]
                ]),
                _cache[16] || (_cache[16] = _createElementVNode("span", null, "Process issue", -1 /* CACHED */))
              ]),
              _createElementVNode("label", _hoisted_12, [
                _cache[17] || (_cache[17] = _createElementVNode("span", null, "Quantity", -1 /* CACHED */)),
                _createVNode(_unref(Input), {
                  modelValue: form.value.quantity,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.quantity) = $event)),
                  type: "number",
                  variant: "form",
                  min: 1,
                  max: 9999,
                  "default-value": 1,
                  "numeric-only": "",
                  disabled: isReadOnly.value || isSaving.value
                }, null, 8 /* PROPS */, ["modelValue", "disabled"])
              ]),
              _createElementVNode("label", _hoisted_13, [
                _cache[18] || (_cache[18] = _createElementVNode("span", null, "Serial No. / Identifiers", -1 /* CACHED */)),
                _createVNode(_unref(Input), {
                  modelValue: form.value.identifier,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.value.identifier) = $event)),
                  variant: "form",
                  disabled: isReadOnly.value || isSaving.value
                }, null, 8 /* PROPS */, ["modelValue", "disabled"])
              ]),
              _createElementVNode("label", _hoisted_14, [
                _cache[19] || (_cache[19] = _createElementVNode("span", null, "*Severity", -1 /* CACHED */)),
                _createVNode(_unref(Select), {
                  modelValue: form.value.severity,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((form.value.severity) = $event)),
                  options: severityOptions,
                  placeholder: "-- Please Select --",
                  variant: "form",
                  "aria-label": "Severity",
                  disabled: isReadOnly.value || isSaving.value
                }, null, 8 /* PROPS */, ["modelValue", "disabled"])
              ]),
              _createElementVNode("label", _hoisted_15, [
                _cache[20] || (_cache[20] = _createElementVNode("span", null, "*Description of problem", -1 /* CACHED */)),
                _createVNode(_unref(TextArea), {
                  modelValue: form.value.description,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((form.value.description) = $event)),
                  variant: "form",
                  disabled: isReadOnly.value || isSaving.value
                }, null, 8 /* PROPS */, ["modelValue", "disabled"])
              ]),
              _createElementVNode("label", _hoisted_16, [
                _cache[21] || (_cache[21] = _createElementVNode("span", null, "*Stage", -1 /* CACHED */)),
                _createVNode(_unref(Select), {
                  modelValue: form.value.stage,
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((form.value.stage) = $event)),
                  options: stageOptions,
                  placeholder: "-- Please Select --",
                  variant: "form",
                  "aria-label": "Stage",
                  disabled: isReadOnly.value || isSaving.value
                }, null, 8 /* PROPS */, ["modelValue", "disabled"])
              ]),
              _createElementVNode("label", _hoisted_17, [
                _cache[22] || (_cache[22] = _createElementVNode("span", null, "WO / PO Reference", -1 /* CACHED */)),
                _createVNode(_unref(Input), {
                  modelValue: form.value.ref,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((form.value.ref) = $event)),
                  variant: "form",
                  disabled: isReadOnly.value || isSaving.value
                }, null, 8 /* PROPS */, ["modelValue", "disabled"])
              ]),
              _createElementVNode("label", _hoisted_18, [
                _cache[23] || (_cache[23] = _createElementVNode("span", null, "*Time Lost (min)", -1 /* CACHED */)),
                _createVNode(_unref(Input), {
                  modelValue: form.value.timelost,
                  "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((form.value.timelost) = $event)),
                  type: "number",
                  variant: "form",
                  disabled: isReadOnly.value || isSaving.value
                }, null, 8 /* PROPS */, ["modelValue", "disabled"])
              ])
            ]),
            (mode.value === 'create')
              ? (_openBlock(), _createElementBlock("section", _hoisted_19, [
                  _cache[24] || (_cache[24] = _createElementVNode("label", {
                    class: "ncr2-create-file-button",
                    for: "ncr2-create-files"
                  }, [
                    _createElementVNode("i", { class: "material-icons" }, "attach_file"),
                    _createTextVNode(" Attachment ")
                  ], -1 /* CACHED */)),
                  _createElementVNode("input", {
                    id: "ncr2-create-files",
                    type: "file",
                    multiple: "",
                    disabled: isSaving.value,
                    onChange: stageFiles
                  }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_20),
                  (stagedFiles.value.length)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(stagedFiles.value, (file) => {
                          return (_openBlock(), _createElementBlock("span", {
                            key: `${file.name}-${file.size}`
                          }, _toDisplayString(file.name), 1 /* TEXT */))
                        }), 128 /* KEYED_FRAGMENT */))
                      ]))
                    : _createCommentVNode("v-if", true)
                ]))
              : (_openBlock(), _createElementBlock("section", _hoisted_22, [
                  _createElementVNode("nav", _hoisted_23, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(tabs, (tab) => {
                      return _createElementVNode("button", {
                        key: tab.key,
                        type: "button",
                        class: _normalizeClass({ active: activeTab.value === tab.key }),
                        onClick: ($event: any) => (activeTab.value = tab.key)
                      }, [
                        _createTextVNode(_toDisplayString(tab.label) + " ", 1 /* TEXT */),
                        (tab.key === 'files' && fileCount.value)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_25, _toDisplayString(fileCount.value), 1 /* TEXT */))
                          : _createCommentVNode("v-if", true)
                      ], 10 /* CLASS, PROPS */, _hoisted_24)
                    }), 64 /* STABLE_FRAGMENT */))
                  ]),
                  _createElementVNode("div", _hoisted_26, [
                    (activeTab.value === 'messages')
                      ? (_openBlock(), _createBlock(NcrMessagesPane, {
                          key: 0,
                          "record-id": recordId.value,
                          readonly: isReadOnly.value
                        }, null, 8 /* PROPS */, ["record-id", "readonly"]))
                      : (activeTab.value === 'capa')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                            _createElementVNode("label", _hoisted_28, [
                              _cache[25] || (_cache[25] = _createElementVNode("span", null, "Corrective / Preventive Action", -1 /* CACHED */)),
                              _createVNode(_unref(TextArea), {
                                modelValue: form.value.capa,
                                "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((form.value.capa) = $event)),
                                variant: "form",
                                disabled: isReadOnly.value || isSaving.value
                              }, null, 8 /* PROPS */, ["modelValue", "disabled"])
                            ]),
                            _createElementVNode("label", _hoisted_29, [
                              _cache[26] || (_cache[26] = _createElementVNode("span", null, "Issue", -1 /* CACHED */)),
                              _createVNode(_unref(Select), {
                                modelValue: form.value.issue,
                                "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((form.value.issue) = $event)),
                                options: issueOptions.value,
                                placeholder: "Link issue",
                                variant: "form",
                                "aria-label": "Linked issue",
                                disabled: isReadOnly.value || isSaving.value
                              }, null, 8 /* PROPS */, ["modelValue", "options", "disabled"])
                            ]),
                            _createElementVNode("label", _hoisted_30, [
                              _cache[27] || (_cache[27] = _createElementVNode("span", null, "Asana ID", -1 /* CACHED */)),
                              _createVNode(_unref(Input), {
                                modelValue: form.value.asana,
                                "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((form.value.asana) = $event)),
                                variant: "form",
                                disabled: isReadOnly.value || isSaving.value
                              }, null, 8 /* PROPS */, ["modelValue", "disabled"]),
                              (form.value.asana)
                                ? (_openBlock(), _createElementBlock("a", {
                                    key: 0,
                                    href: asanaUrl.value,
                                    target: "_blank",
                                    rel: "noopener"
                                  }, "Open in Asana", 8 /* PROPS */, _hoisted_31))
                                : _createCommentVNode("v-if", true)
                            ]),
                            _createElementVNode("label", _hoisted_32, [
                              _cache[28] || (_cache[28] = _createElementVNode("span", null, "Status", -1 /* CACHED */)),
                              _createVNode(_unref(Select), {
                                modelValue: form.value.status,
                                "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((form.value.status) = $event)),
                                options: statusOptions,
                                variant: "form",
                                "aria-label": "Status",
                                disabled: isReadOnly.value || isSaving.value
                              }, null, 8 /* PROPS */, ["modelValue", "disabled"])
                            ]),
                            _createElementVNode("label", _hoisted_33, [
                              _cache[29] || (_cache[29] = _createElementVNode("span", null, "Disposition", -1 /* CACHED */)),
                              _createVNode(_unref(Select), {
                                modelValue: form.value.disposition,
                                "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((form.value.disposition) = $event)),
                                options: dispositionOptions,
                                placeholder: "-- Please Select --",
                                variant: "form",
                                "aria-label": "Disposition",
                                disabled: isReadOnly.value || isSaving.value
                              }, null, 8 /* PROPS */, ["modelValue", "disabled"])
                            ])
                          ]))
                        : (activeTab.value === 'files')
                          ? (_openBlock(), _createBlock(NcrFilesPane, {
                              key: 2,
                              "record-id": recordId.value,
                              "legacy-files": record.value?.files || [],
                              readonly: isReadOnly.value,
                              onRemoveLegacy: removeLegacyFile
                            }, null, 8 /* PROPS */, ["record-id", "legacy-files", "readonly"]))
                          : (_openBlock(), _createBlock(NcrActivityPane, {
                              key: 3,
                              "record-id": recordId.value
                            }, null, 8 /* PROPS */, ["record-id"]))
                  ])
                ]))
          ]),
          _createElementVNode("footer", _hoisted_34, [
            _createElementVNode("label", _hoisted_35, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((form.value.ncr_shelf) = $event)),
                type: "checkbox",
                disabled: isReadOnly.value || isSaving.value
              }, null, 8 /* PROPS */, _hoisted_36), [
                [_vModelCheckbox, form.value.ncr_shelf]
              ]),
              _cache[30] || (_cache[30] = _createElementVNode("span", null, "Item(s) on quarantine shelf", -1 /* CACHED */))
            ]),
            _createElementVNode("div", _hoisted_37, [
              (mode.value !== 'create')
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: "button pill -text",
                    disabled: isPrinting.value,
                    onClick: printLabel
                  }, [
                    _cache[31] || (_cache[31] = _createElementVNode("i", { class: "material-icons" }, "receipt_long", -1 /* CACHED */)),
                    _createTextVNode(" " + _toDisplayString(isPrinting.value ? 'Queuing…' : 'Print'), 1 /* TEXT */)
                  ], 8 /* PROPS */, _hoisted_38))
                : _createCommentVNode("v-if", true),
              (!isReadOnly.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 1,
                    type: "submit",
                    class: "button pill -blue",
                    disabled: isSaving.value
                  }, _toDisplayString(isSaving.value ? 'Saving…' : 'Save'), 9 /* TEXT, PROPS */, _hoisted_39))
                : _createCommentVNode("v-if", true),
              _createElementVNode("button", {
                type: "button",
                class: "button pill -text",
                onClick: close
              }, "Cancel")
            ])
          ])
        ], 32 /* NEED_HYDRATION */)
      ])
    ])
  ]))
}
}

})